import { useParams } from 'react-router';
import { Async } from '../../components/Async.tsx';
import { BackButton } from '../../components/BackButton.tsx';
import { BarList } from '../../components/charts/BarList.tsx';
import { FunnelBars } from '../../components/charts/FunnelBars.tsx';
import { TrendChart } from '../../components/charts/TrendChart.tsx';
import { ImmatureCohortNotice } from '../../components/ImmatureCohortNotice.tsx';
import { EmptyState, Panel } from '../../components/States.tsx';
import { formatDuration, formatNumber, formatPercent } from '../../lib/format.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import { usePageTitle } from '../../lib/page-title.ts';
import { useSalesperson } from '../../lib/queries.ts';
import { useDashboardParams } from '../../lib/url-state.ts';
import { Controls } from '../controls/Controls.tsx';

export function SalespersonPage() {
  const t = useT();
  const locale = useLocale();
  const { id = '' } = useParams();
  const [params] = useDashboardParams();
  const query = useSalesperson(id, params);
  const name = query.data?.data.salesperson.fullName ?? t.team.title;
  usePageTitle(name);
  const n = (value: number): string => formatNumber(value, locale);

  return (
    <>
      <BackButton fallback="/team" />
      <h1 className="text-2xl font-bold tracking-tight">{name}</h1>
      <Controls meta={query.data?.meta} compare={false} filters={false} />
      {query.error?.status === 404 ? (
        <EmptyState title={t.errors.notFoundTitle} description={t.errors.unknownSalesperson} />
      ) : (
        <Async query={query}>
          {({ data }) => {
            const m = data.metrics;
            const stats: [string, string][] = [
              [t.team.columns.leadsAssigned, n(m.leadsAssigned)],
              [t.team.columns.leadsContacted, n(m.leadsContacted)],
              [t.team.columns.leadsQualified, n(m.leadsQualified)],
              [t.team.columns.visitsPlanned, n(m.visitsPlanned)],
              ...(data.visitAvailability.completed
                ? ([[t.team.columns.visitsCompleted, n(m.visitsCompleted)]] as [string, string][])
                : []),
              [t.team.columns.dealsWon, n(m.dealsWon)],
              [t.team.columns.containersSold, n(m.containersSold)],
              [t.team.columns.cohortConversion, formatPercent(m.cohort.overallConversion, locale)],
              [
                t.team.columns.firstResponse,
                formatDuration(m.firstResponse.medianMinutes, locale, {
                  minutes: t.units.minutes,
                  hours: t.units.hours,
                }),
              ],
              [t.team.columns.followUpOnTime, formatPercent(m.followUpOnTime.rate, locale)],
              [t.team.columns.followUpsOverdue, n(m.followUpsOverdue)],
            ];
            return (
              <>
                <Panel title={t.team.detail.metrics}>
                  <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {stats.map(([label, value]) => (
                      <div key={label}>
                        <dt className="text-xs text-muted-foreground">{label}</dt>
                        <dd className="text-xl font-semibold tabular-nums">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </Panel>
                <div className="grid gap-4 lg:grid-cols-2">
                  <Panel title={t.team.detail.funnel}>
                    <ImmatureCohortNotice maturity={data.funnel.maturity} />
                    <FunnelBars
                      steps={data.funnel.steps.map((s) => ({
                        milestone: s.milestone,
                        label: t.funnel.steps[s.milestone],
                        count: s.count,
                        optional: s.optional,
                      }))}
                    />
                  </Panel>
                  <Panel title={t.team.detail.trend}>
                    <TrendChart
                      title={t.team.detail.trend}
                      series={data.trend.series.map((s) => ({
                        key: s.metric,
                        label: t.trendMetrics[s.metric],
                        points: s.points,
                      }))}
                    />
                  </Panel>
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <Panel title={t.team.detail.overdue}>
                    <BarList
                      ariaLabel={t.team.detail.overdue}
                      unit={t.units.tasks}
                      items={(['lt1d', 'd1to3', 'gt3d'] as const).map((key) => ({
                        key,
                        label: t.team.detail.ages[key],
                        value: data.overdueByAge[key],
                      }))}
                    />
                  </Panel>
                  <Panel title={t.team.detail.reassignments}>
                    <BarList
                      ariaLabel={t.team.detail.reassignments}
                      unit={t.units.leads}
                      items={[
                        {
                          key: 'in',
                          label: t.team.detail.reassignedIn,
                          value: data.reassignments.in,
                        },
                        {
                          key: 'out',
                          label: t.team.detail.reassignedOut,
                          value: data.reassignments.out,
                        },
                      ]}
                    />
                  </Panel>
                </div>
              </>
            );
          }}
        </Async>
      )}
    </>
  );
}
